import { BLOG_CMS_IFRAME_SANDBOX } from "./protocol";

/**
 * U9: admin logout also ends the editor session. Admin learns the editor origin only from a C2 url,
 * and `logout` runs from the avatar menu on any page, often after a reload, so each human ticket
 * stores the origin (never the ticket or the url). The origin is public config.
 */
export const BLOG_CMS_ORIGIN_KEY = "blog_cms_origin";
export const BLOG_CMS_LOGOUT_TIMEOUT_MS = 2_000;

export function rememberBlogCmsOrigin(origin: string): void {
  try {
    localStorage.setItem(BLOG_CMS_ORIGIN_KEY, origin);
  } catch {
    // Storage can be blocked; sign-out then degrades to the 1 h C5 lifetime.
  }
}

function forgetBlogCmsOrigin(): void {
  try {
    localStorage.removeItem(BLOG_CMS_ORIGIN_KEY);
  } catch {
    // Nothing to do: the next logout reads the same value and fails the same way.
  }
}

function readStoredOrigin(): string | null {
  try {
    return localStorage.getItem(BLOG_CMS_ORIGIN_KEY);
  } catch {
    return null;
  }
}

function isTrustedOrigin(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.origin !== value) return false;
  return url.protocol === "https:" || (url.protocol === "http:" && url.hostname === "localhost");
}

/**
 * Loads `<origin>/_sso/logout` in a hidden frame (C6), which clears both host-only editor cookies,
 * and resolves on its `load` or after 2 s, whichever comes first. It never rejects, so an editor
 * failure can never block an admin logout.
 */
export async function clearBlogCmsSession(): Promise<void> {
  const origin = readStoredOrigin();
  if (origin === null) return;
  if (!isTrustedOrigin(origin)) {
    forgetBlogCmsOrigin();
    return;
  }

  const frame = document.createElement("iframe");
  frame.setAttribute("sandbox", BLOG_CMS_IFRAME_SANDBOX);
  frame.hidden = true;
  frame.setAttribute("aria-hidden", "true");
  frame.tabIndex = -1;
  frame.title = "Blog editor sign-out";

  await new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, BLOG_CMS_LOGOUT_TIMEOUT_MS);
    frame.addEventListener(
      "load",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
    frame.src = `${origin}/_sso/logout`;
    document.body.appendChild(frame);
  });

  frame.remove();
  forgetBlogCmsOrigin();
}
